import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  X, Shield, User as UserIcon, Mail, CheckCircle2, AlertCircle,
  Loader2, Save, RotateCcw, Sparkles, KeyRound, Fingerprint,
  Users, Crown, Power
} from 'lucide-react';
import { UsersService, type User, type UpdateUserDto, type Role } from '../../services/users/users.service';

const editUserSchema = z.object({
  nombreCompleto: z.string().min(1, 'Nombre completo es requerido').min(2, 'Mínimo 2 caracteres'),
  tipoUsuario: z.enum(['administrador', 'miembro']),
  estado: z.boolean(),
  requiereCambioContrasena: z.boolean(),
  rolId: z.string().min(1, 'Debe seleccionar un rol')
});

type EditUserFormData = z.infer<typeof editUserSchema>;

interface EditUserDrawerProps {
  isOpen: boolean;
  user: User | null;
  onClose: () => void;
  onSuccess?: () => void;
  onUpdateResult?: (success: boolean, message: string) => void;
}

export const EditUserDrawer: React.FC<EditUserDrawerProps> = ({
  isOpen,
  user,
  onClose,
  onSuccess,
  onUpdateResult
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    watch,
    setValue
  } = useForm<EditUserFormData>({
    resolver: zodResolver(editUserSchema)
  });

  const tipoUsuario = watch('tipoUsuario');
  const estado = watch('estado');
  const rolId = watch('rolId');

  // Cargar roles disponibles
  useEffect(() => {
    const loadRoles = async () => {
      setIsLoadingRoles(true);
      try {
        const rolesData = await UsersService.getRoles();
        setRoles(rolesData.filter(r => r.activo));
      } catch (error) {
        console.error('Error al cargar roles:', error);
      } finally {
        setIsLoadingRoles(false);
      }
    };
    if (isOpen) loadRoles();
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && user) {
      const currentRoleId = user.roles?.find(r => r.activo)?.id || '';
      reset({
        nombreCompleto: user.nombreCompleto,
        tipoUsuario: user.tipoUsuario,
        estado: user.estado,
        requiereCambioContrasena: user.requiereCambioContrasena,
        rolId: currentRoleId
      });
      const timer = setTimeout(() => setIsVisible(true), 10);
      return () => clearTimeout(timer);
    } else if (!isOpen) {
      setIsVisible(false);
      setTimeout(() => {
        setIsLoading(false);
        setErrorMessage(null);
        setSuccessMessage(null);
        reset();
      }, 300);
    }
  }, [isOpen, user, reset]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => onClose(), 300);
  };

  const onSubmit = async (data: EditUserFormData) => {
    if (!user) return;
    setIsLoading(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      const updateData: UpdateUserDto = {
        nombreCompleto: data.nombreCompleto,
        tipoUsuario: data.tipoUsuario,
        estado: data.estado,
        requiereCambioContrasena: data.requiereCambioContrasena,
        roles: data.rolId ? [data.rolId] : undefined
      };

      await UsersService.updateUser(user.id, updateData);
      setSuccessMessage('Usuario actualizado exitosamente');

      if (onUpdateResult) {
        onUpdateResult(true, `Los datos de ${user.nombreCompleto} fueron actualizados.`);
      }
      if (onSuccess) onSuccess();

      setTimeout(() => handleClose(), 1200);
    } catch (error: any) {
      const backendMessage = error?.response?.data?.message;
      const errorMsg =
        backendMessage ||
        (error instanceof Error
          ? error.message
          : 'Error desconocido al actualizar usuario');
      setErrorMessage(errorMsg);
      if (onUpdateResult) onUpdateResult(false, errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const selectedRole = roles.find(r => r.id === rolId);
  const initials = user ? `${user.nombreCompleto.charAt(0).toUpperCase()}${user.nombreCompleto.split(' ')[1]?.charAt(0)?.toUpperCase() || ''}` : '';

  const tiposUsuario = [
    { value: 'miembro' as const, label: 'Miembro', icon: <Users className="w-4 h-4" />, desc: 'Acceso limitado al sistema' },
    { value: 'administrador' as const, label: 'Administrador', icon: <Crown className="w-4 h-4" />, desc: 'Control total del sistema' }
  ];

  if (!isOpen || !user) return null;

  return (
    <div className="fixed left-0 right-0 bottom-0 top-0 z-[60] overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        {/* Overlay */}
        <div
          className={`absolute inset-0 transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
          onClick={handleClose}
        />

        {/* Drawer */}
        <section className={`absolute right-0 top-0 h-full w-full max-w-4xl border-l border-gray-200 transform transition-transform duration-300 ease-in-out ${
          isVisible ? 'translate-x-0' : 'translate-x-full'
        }`}>
          <div className="h-full flex flex-col bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>

            {/* ═══ HEADER CON AVATAR ═══ */}
            <div className="px-6 py-5 bg-white border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-purple-500 to-blue-600 flex items-center justify-center shadow-lg shadow-purple-200">
                    <span className="text-white text-xs font-medium">{initials}</span>
                  </div>
                  <div>
                    <h2 className="text-xs font-medium text-gray-900 leading-tight">Editar Usuario</h2>
                    <p className="text-xs text-gray-500 mt-0.5">{user.email}</p>
                  </div>
                </div>
                <button onClick={handleClose} className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>
              {/* Badges */}
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                {/* Rol — principal */}
                {user.roles && user.roles.length > 0 && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md bg-purple-100 text-purple-800 border border-purple-300">
                    <Shield className="w-3.5 h-3.5" />
                    {(user.roles.find(r => r.activo) || user.roles[0])?.nombre}
                  </span>
                )}
                {/* Tipo de usuario — secundario */}
                <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[11px] font-medium rounded-md border ${
                  user.tipoUsuario === 'administrador'
                    ? 'bg-blue-50 text-blue-700 border-blue-200'
                    : 'bg-green-50 text-green-700 border-green-200'
                }`}>
                  {user.tipoUsuario === 'administrador' ? <Crown className="w-3 h-3" /> : <Users className="w-3 h-3" />}
                  {user.tipoUsuario === 'administrador' ? 'Administrador' : 'Miembro'}
                </span>
                {user.estado ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-md bg-green-50 text-green-700 border border-green-200">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" /> Activo
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-md bg-red-50 text-red-700 border border-red-200">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500" /> Inactivo
                  </span>
                )}
              </div>
            </div>

            {/* ═══ MENSAJES ═══ */}
            <div className="px-6 pt-4">
              {errorMessage && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3 animate-in fade-in slide-in-from-top-1">
                  <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                  <p className="text-xs text-red-700 font-medium">{errorMessage}</p>
                </div>
              )}
              {successMessage && (
                <div className="mb-4 bg-green-50 border border-green-200 rounded-xl p-4 flex items-start gap-3 animate-in fade-in slide-in-from-top-1">
                  <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                  <p className="text-xs text-green-700 font-medium">{successMessage}</p>
                </div>
              )}
            </div>

            {/* ═══ FORMULARIO ═══ */}
            <div className="flex-1 overflow-y-auto">
              <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-5">

                {/* ─── Card: Información Personal ─── */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  <div className="px-5 py-3 bg-gray-50/80 border-b border-gray-100 flex items-center gap-2">
                    <UserIcon className="w-4 h-4 text-purple-600" />
                    <h3 className="text-xs font-medium text-gray-900">Información Personal</h3>
                  </div>
                  <div className="p-5 space-y-4">
                    {/* Nombre completo */}
                    <div>
                      <label className="flex items-center gap-1.5 text-xs font-medium text-gray-700 uppercase tracking-wide mb-2">
                        <UserIcon className="w-3.5 h-3.5" /> Nombre Completo <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        {...register('nombreCompleto')}
                        placeholder="Ej. Juan Carlos Pérez"
                        className={`w-full px-4 py-2.5 text-xs border rounded-lg transition-all focus:ring-4 focus:outline-none ${
                          errors.nombreCompleto
                            ? 'border-red-400 focus:border-red-500 focus:ring-red-500/15 bg-red-50/30'
                            : 'border-gray-200 focus:border-purple-500 focus:ring-purple-500/15 bg-white'
                        }`}
                      />
                      {errors.nombreCompleto && (
                        <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" /> {errors.nombreCompleto.message}
                        </p>
                      )}
                    </div>

                    {/* Email (no editable) */}
                    <div>
                      <label className="flex items-center gap-1.5 text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                        <Mail className="w-3.5 h-3.5" /> Correo Electrónico
                      </label>
                      <div className="relative">
                        <input
                          type="email"
                          value={user.email}
                          disabled
                          className="w-full px-4 py-2.5 text-xs border border-gray-200 rounded-lg bg-gray-50 text-gray-400 cursor-not-allowed font-mono"
                        />
                        <span className="absolute inset-y-0 right-0 pr-3 flex items-center">
                          <span className="text-[10px] font-medium text-gray-400 bg-gray-100 px-2 py-0.5 rounded border border-gray-200 uppercase tracking-wider">No editable</span>
                        </span>
                      </div>
                      <p className="mt-1.5 text-xs text-gray-400">El correo no puede modificarse por seguridad</p>
                    </div>
                  </div>
                </div>

                {/* ─── Card: Configuración de Acceso ─── */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  <div className="px-5 py-3 bg-gray-50/80 border-b border-gray-100 flex items-center gap-2">
                    <Shield className="w-4 h-4 text-purple-600" />
                    <h3 className="text-xs font-medium text-gray-900">Configuración de Acceso</h3>
                  </div>
                  <div className="p-5 space-y-5">

                    {/* Tipo de usuario — Cards seleccionables */}
                    <div>
                      <label className="flex items-center gap-1.5 text-xs font-medium text-gray-700 uppercase tracking-wide mb-3">
                        <Sparkles className="w-3.5 h-3.5" /> Tipo de Usuario <span className="text-red-500">*</span>
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        {tiposUsuario.map((tipo) => (
                          <button
                            key={tipo.value}
                            type="button"
                            onClick={() => setValue('tipoUsuario', tipo.value, { shouldDirty: true })}
                            className={`relative flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all text-center ${
                              tipoUsuario === tipo.value
                                ? 'border-purple-500 bg-purple-50/50 shadow-sm shadow-purple-100'
                                : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50/50'
                            }`}
                          >
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                              tipoUsuario === tipo.value ? 'bg-purple-100 text-purple-600' : 'bg-gray-100 text-gray-500'
                            }`}>
                              {tipo.icon}
                            </div>
                            <div>
                              <span className={`block text-xs font-medium ${tipoUsuario === tipo.value ? 'text-purple-700' : 'text-gray-700'}`}>
                                {tipo.label}
                              </span>
                              <span className="block text-xs text-gray-500 mt-0.5">{tipo.desc}</span>
                            </div>
                            {tipoUsuario === tipo.value && (
                              <div className="absolute top-2 right-2">
                                <CheckCircle2 className="w-4 h-4 text-purple-500" />
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                      <input type="hidden" {...register('tipoUsuario')} />
                      {errors.tipoUsuario && (
                        <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" /> {errors.tipoUsuario.message}
                        </p>
                      )}
                    </div>

                    {/* Rol — Cards seleccionables */}
                    <div>
                      <label className="flex items-center gap-1.5 text-xs font-medium text-gray-700 uppercase tracking-wide mb-3">
                        <Shield className="w-3.5 h-3.5" /> Rol del Usuario <span className="text-red-500">*</span>
                      </label>

                      {isLoadingRoles ? (
                        <div className="flex items-center gap-3 p-6 rounded-xl border-2 border-dashed border-gray-200 bg-gray-50/50">
                          <Loader2 className="w-5 h-5 text-purple-500 animate-spin" />
                          <span className="text-xs text-gray-500">Cargando roles disponibles...</span>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {roles.map((rol) => {
                            const isSelected = rolId === rol.id;
                            const isCurrent = user.roles?.some(r => r.id === rol.id);
                            return (
                              <button
                                key={rol.id}
                                type="button"
                                onClick={() => setValue('rolId', rol.id, { shouldDirty: true })}
                                className={`relative flex items-start gap-3 p-4 rounded-xl border-2 text-left transition-all ${
                                  isSelected
                                    ? 'border-purple-500 bg-purple-50/60 shadow-sm shadow-purple-100'
                                    : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50/50'
                                }`}
                              >
                                {/* Icono del rol */}
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 transition-colors ${
                                  isSelected ? 'bg-purple-100 text-purple-600' : 'bg-gray-100 text-gray-500'
                                }`}>
                                  <Shield className="w-5 h-5" />
                                </div>

                                {/* Info del rol */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className={`block text-xs font-medium ${isSelected ? 'text-purple-700' : 'text-gray-900'}`}>
                                      {rol.nombre}
                                    </span>
                                    {isCurrent && (
                                      <span className="px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider bg-green-100 text-green-700 rounded-full border border-green-200 shrink-0">
                                        Actual
                                      </span>
                                    )}
                                  </div>
                                  {rol.descripcion && (
                                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{rol.descripcion}</p>
                                  )}
                                </div>

                                {/* Check */}
                                {isSelected && (
                                  <div className="absolute top-2 right-2">
                                    <div className="w-5 h-5 rounded-full bg-purple-500 flex items-center justify-center">
                                      <CheckCircle2 className="w-3.5 h-3.5 text-white" />
                                    </div>
                                  </div>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      )}

                      <input type="hidden" {...register('rolId')} />
                      {errors.rolId && (
                        <p className="mt-2 text-xs text-red-600 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" /> {errors.rolId.message}
                        </p>
                      )}
                    </div>

                    {/* Toggle switches */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                      <ToggleCard
                        icon={<Power className="w-4 h-4" />}
                        label="Usuario activo"
                        description="Permite el acceso al sistema"
                        checked={estado}
                        onChange={(v) => setValue('estado', v, { shouldDirty: true })}
                        activeColor="green"
                      />
                      <ToggleCard
                        icon={<KeyRound className="w-4 h-4" />}
                        label="Requiere cambio de contraseña"
                        description="Forzar cambio en próximo login"
                        checked={watch('requiereCambioContrasena')}
                        onChange={(v) => setValue('requiereCambioContrasena', v, { shouldDirty: true })}
                        activeColor="amber"
                      />
                    </div>
                  </div>
                </div>

                {/* ─── Card: Sistema (colapsable) ─── */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  <details className="group">
                    <summary className="px-5 py-3 bg-gray-50/80 border-b border-gray-100 flex items-center justify-between cursor-pointer select-none list-none">
                      <div className="flex items-center gap-2">
                        <Fingerprint className="w-4 h-4 text-gray-500" />
                        <span className="text-xs font-medium text-gray-700">Información del Sistema</span>
                      </div>
                      <svg className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                    </summary>
                    <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-500 font-medium">ID:</span>
                        <code className="font-mono text-gray-700 bg-gray-100 px-2 py-0.5 rounded">{user.id}</code>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-500 font-medium">Creado:</span>
                        <span className="text-gray-700">{new Date(user.creadoEn).toLocaleString('es-ES')}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-500 font-medium">Actualizado:</span>
                        <span className="text-gray-700">{new Date(user.actualizadoEn).toLocaleString('es-ES')}</span>
                      </div>
                      {user.ultimoAcceso && (
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500 font-medium">Último acceso:</span>
                          <span className="text-gray-700">{new Date(user.ultimoAcceso).toLocaleString('es-ES')}</span>
                        </div>
                      )}
                    </div>
                  </details>
                </div>
              </form>
            </div>

            {/* ═══ FOOTER ═══ */}
            <div className="px-6 py-4 bg-white border-t border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                {!isDirty && !isLoading && !successMessage && (
                  <span className="text-xs text-gray-400 flex items-center gap-1">
                    <Fingerprint className="w-3 h-3" /> Sin cambios
                  </span>
                )}
                {isDirty && !isLoading && !successMessage && (
                  <span className="text-xs text-amber-600 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> Hay cambios sin guardar
                  </span>
                )}
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={isLoading}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors disabled:opacity-50"
                >
                  <RotateCcw className="w-4 h-4" /> Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleSubmit(onSubmit)}
                  disabled={isLoading || !!successMessage || !isDirty}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-xs font-medium bg-purple-600 text-white hover:bg-purple-700 transition-colors shadow-sm shadow-purple-200 disabled:bg-gray-300 disabled:shadow-none disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Guardando...</>
                  ) : successMessage ? (
                    <><CheckCircle2 className="w-4 h-4" /> Guardado</>
                  ) : (
                    <><Save className="w-4 h-4" /> Guardar Cambios</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════
   COMPONENTE AUXILIAR: ToggleCard
   ═══════════════════════════════════════════ */
interface ToggleCardProps {
  icon: React.ReactNode;
  label: string;
  description: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  activeColor: 'green' | 'amber' | 'purple';
}

const ToggleCard: React.FC<ToggleCardProps> = ({ icon, label, description, checked, onChange, activeColor }) => {
  const colorMap = {
    green: { bg: 'bg-green-500', ring: 'ring-green-500/30', dot: 'bg-green-50 text-green-600' },
    amber: { bg: 'bg-amber-500', ring: 'ring-amber-500/30', dot: 'bg-amber-50 text-amber-600' },
    purple: { bg: 'bg-purple-500', ring: 'ring-purple-500/30', dot: 'bg-purple-50 text-purple-600' },
  };
  const c = colorMap[activeColor];

  return (
    <div
      onClick={() => onChange(!checked)}
      className={`flex items-center gap-3 p-3.5 rounded-xl border-2 cursor-pointer transition-all select-none ${
        checked ? `border-${activeColor}-200 bg-${activeColor}-50/30` : 'border-gray-200 bg-white hover:border-gray-300'
      }`}
    >
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${checked ? c.dot : 'bg-gray-100 text-gray-500'}`}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-gray-900">{label}</p>
        <p className="text-xs text-gray-500">{description}</p>
      </div>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onChange(!checked); }}
        className={`relative w-11 h-6 rounded-full transition-colors duration-200 focus:outline-none focus:ring-4 ${c.ring} ${checked ? c.bg : 'bg-gray-300'}`}
      >
        <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${checked ? 'translate-x-5' : 'translate-x-0'}`} />
      </button>
    </div>
  );
};

export default EditUserDrawer;
